import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronRight, Store, Mail, Loader2, FileText, DollarSign,
  Wrench, Car, CheckCircle2, Circle, ChevronDown, ChevronUp,
  Settings, X, Eye, Send, Building2, Paperclip, Upload, Printer, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { API_URL as _API_URL } from '@/lib/apiUrl';
import { urlFichier } from '@/lib/fichiers';

const API = _API_URL;
const fmt = v => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(v || 0);
const MOIS_FR = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const STATUT_LABELS = { brouillon: "Brouillon", envoyee: "Envoyée", payee: "Payée", ajustee: "Ajustée" };
const STATUT_COLORS = {
  brouillon: "bg-slate-100 text-slate-700",
  envoyee: "bg-blue-100 text-blue-700",
  payee: "bg-emerald-100 text-emerald-700",
  ajustee: "bg-amber-100 text-amber-700",
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")}` };
}

// ─── Generate printable HTML (same format as client dossier invoices) ────
function generateLMDSInvoiceHTML(preview) {
  const f = preview.facture;
  const client = preview.client || {};
  const ent = preview.entreprise || {};
  const inst = preview.installer || {};

  const logoUrl = ent.logo_principal ? `${API}${ent.logo_principal}` : null;

  const rowsSC = (f.lignes || []).map(l => `
    <tr>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px;">S.${l.semaine}</td>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px;">${l.nom_client || "—"}</td>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px;">${l.date_rdv || "—"}</td>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px;">${l.si || "—"}</td>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px;">${l.description || "—"}</td>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px; text-align:right; font-weight:600;">${l.service_call > 0 ? fmt(l.service_call) : "—"}</td>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px; text-align:right;">${l.km_montant > 0 ? fmt(l.km_montant) : "—"}</td>
      <td style="padding:6px 12px; border-bottom:1px solid #e5e7eb; font-size:11px; text-align:right; font-weight:600;">${fmt((l.service_call || 0) + (l.km_montant || 0))}</td>
    </tr>`).join('');

  const notesHtml = f.notes ? `
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <h3 style="font-size:14px; font-weight:700; margin-bottom:10px;">Notes</h3>
      <p style="font-size:11px; color:#374151; white-space:pre-wrap; line-height:1.7;">${f.notes}</p>
    </div>` : '';

  const adjustedHtml = f.ajuste ? `
    <tr>
      <td style="padding:7px 12px; font-size:12px; color:#6b7280; text-decoration:line-through; border-bottom:1px solid #e5e7eb;">Sous-total original</td>
      <td style="padding:7px 12px; font-size:12px; text-align:right; color:#6b7280; text-decoration:line-through; border-bottom:1px solid #e5e7eb;">${fmt(f.sous_total_original)}</td>
    </tr>` : '';

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>FACTURE SERVICE CALL – ${f.numero || "Brouillon"}</title>
  <style>
    @page { size: letter; margin: 0.65in 0.7in; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #111; line-height: 1.5; background: #f3f4f6; }
    .print-page { max-width: 800px; margin: 0 auto; background: white; padding: 24px; }
    table { width: 100%; border-collapse: collapse; }
    img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .btn-print {
      display: inline-flex; align-items: center; gap: 8px;
      background: #2563eb; color: white; border: none; padding: 10px 20px;
      font-size: 14px; border-radius: 6px; cursor: pointer; font-weight: 600;
    }
    .btn-print:hover { background: #1d4ed8; }
    @media print {
      .no-print { display: none !important; }
      body { background: white; }
      .print-page { padding: 0; box-shadow: none; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="background:#1e3a5f; color:white; padding:12px 24px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100;">
    <span style="font-size:14px; font-weight:600;">FACTURE SERVICE CALL – ${f.numero || "Brouillon"}</span>
    <button class="btn-print" onclick="window.print()">Imprimer / Enregistrer PDF</button>
  </div>

  <div class="print-page">
    <!-- EN-TÊTE -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <table>
        <tr>
          <td style="width:22%; vertical-align:top; padding-right:16px;">
            ${logoUrl
              ? `<img src="${logoUrl}" alt="Logo" style="width:100%; max-width:200px; max-height:160px; object-fit:contain;">`
              : `<div style="width:180px; height:120px; background:#f3f4f6; border-radius:6px;"></div>`}
          </td>
          <td style="width:50%; vertical-align:top; text-align:center; padding:4px 16px;">
            <p style="font-size:16px; font-weight:800; margin-bottom:8px;">${ent.nom_entreprise || 'Peinture Résidentielle QC Inc.'}</p>
            <p style="font-size:11px; color:#374151;">${ent.adresse || ''}</p>
            <p style="font-size:11px; color:#374151;">${ent.ville || ''} ${ent.code_postal || ''}</p>
            <p style="font-size:12px; font-weight:700; margin-top:6px;">${ent.telephone || ''}</p>
            <p style="font-size:11px; color:#374151;">${ent.email || ''}</p>
          </td>
          <td style="width:28%; vertical-align:top; padding-left:8px;">
            <p style="font-size:13px; font-weight:700;">Installateur : ${inst.prenom} ${inst.nom}</p>
            <p style="margin-top:6px; font-size:11px;"><strong>Facture # :</strong> ${f.numero || "Non généré"}</p>
            <p style="font-size:11px;"><strong>Période :</strong> ${f.mois_label} ${f.annee}</p>
            <p style="font-size:11px;"><strong>Date :</strong> ${f.date_facture}</p>
            <div style="margin-top:10px; padding-top:8px; border-top:1px solid #e5e7eb;">
              ${ent.numero_tps ? `<p style="font-size:10px;"><strong>TPS :</strong> ${ent.numero_tps}</p>` : ''}
              ${ent.numero_tvq ? `<p style="font-size:10px;"><strong>TVQ :</strong> ${ent.numero_tvq}</p>` : ''}
              ${ent.numero_rbq ? `<p style="font-size:10px;"><strong>RBQ :</strong> ${ent.numero_rbq}</p>` : ''}
            </div>
          </td>
        </tr>
      </table>

      <h1 style="font-size:28px; font-weight:900; text-align:center; margin:20px 0; letter-spacing:1px;">FACTURE SERVICE CALL</h1>

      <table>
        <tr>
          <td style="width:50%; vertical-align:top; padding-right:20px;">
            <p style="font-weight:700; margin-bottom:8px; font-size:12px;">Client :</p>
            <p style="font-weight:700; font-size:13px;">${client.nom_entreprise || 'Le Marché du Store'}</p>
            ${client.adresse ? `<p style="font-size:11px;">${client.adresse}</p>` : ''}
            ${client.ville ? `<p style="font-size:11px;">${client.ville}, ${client.province || 'QC'} ${client.code_postal || ''}</p>` : ''}
            ${client.telephone ? `<p style="font-size:11px;">Téléphone : ${client.telephone}</p>` : ''}
            ${client.email ? `<p style="font-size:11px;">Courriel : ${client.email}</p>` : ''}
          </td>
          <td style="width:50%; vertical-align:top; padding-left:20px;">
            ${client.contact_nom ? `<p style="font-weight:700; margin-bottom:8px; font-size:12px;">Attention :</p><p style="font-size:11px;">${client.contact_nom}${client.contact_poste ? ' — ' + client.contact_poste : ''}</p>` : ''}
            ${client.numero_tps ? `<p style="font-size:10px; margin-top:8px;"><strong>TPS client :</strong> ${client.numero_tps}</p>` : ''}
            ${client.numero_tvq ? `<p style="font-size:10px;"><strong>TVQ client :</strong> ${client.numero_tvq}</p>` : ''}
          </td>
        </tr>
      </table>
    </div>

    <!-- SERVICE CALLS -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <h3 style="font-size:14px; font-weight:700; margin-bottom:14px;">Service Calls — ${f.mois_label} ${f.annee}</h3>
      <table>
        <thead>
          <tr style="border-bottom:2px solid #111827;">
            <th style="text-align:left; padding:8px 12px; font-size:10px; font-weight:700;">Sem.</th>
            <th style="text-align:left; padding:8px 12px; font-size:10px; font-weight:700;">Client</th>
            <th style="text-align:left; padding:8px 12px; font-size:10px; font-weight:700;">Date</th>
            <th style="text-align:left; padding:8px 12px; font-size:10px; font-weight:700;">SI</th>
            <th style="text-align:left; padding:8px 12px; font-size:10px; font-weight:700;">Description</th>
            <th style="text-align:right; padding:8px 12px; font-size:10px; font-weight:700;">SC</th>
            <th style="text-align:right; padding:8px 12px; font-size:10px; font-weight:700;">Km</th>
            <th style="text-align:right; padding:8px 12px; font-size:10px; font-weight:700;">Total</th>
          </tr>
        </thead>
        <tbody>${rowsSC}</tbody>
      </table>
    </div>

    <!-- NOTES -->
    ${notesHtml}

    <!-- TOTAUX -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <table style="max-width:360px; margin-left:auto;">
        <tbody>
          ${adjustedHtml}
          <tr>
            <td style="padding:7px 12px; font-size:12px; border-bottom:1px solid #e5e7eb;">Sous-total${f.ajuste ? ' (ajusté)' : ''}</td>
            <td style="padding:7px 12px; font-size:12px; text-align:right; font-weight:600; border-bottom:1px solid #e5e7eb;${f.ajuste ? ' color:#d97706;' : ''}">${fmt(f.sous_total)}</td>
          </tr>
          <tr>
            <td style="padding:7px 12px; font-size:12px; color:#6b7280; border-bottom:1px solid #e5e7eb;">TPS (5%)</td>
            <td style="padding:7px 12px; font-size:12px; text-align:right; color:#6b7280; border-bottom:1px solid #e5e7eb;">${fmt(f.tps)}</td>
          </tr>
          <tr>
            <td style="padding:7px 12px; font-size:12px; color:#6b7280; border-bottom:2px solid #111827;">TVQ (9,975%)</td>
            <td style="padding:7px 12px; font-size:12px; text-align:right; color:#6b7280; border-bottom:2px solid #111827;">${fmt(f.tvq)}</td>
          </tr>
          <tr>
            <td style="padding:10px 12px; font-size:15px; font-weight:700;">TOTAL</td>
            <td style="padding:10px 12px; font-size:15px; font-weight:700; text-align:right;">${fmt(f.total_avec_taxes)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- MODALITÉS DE PAIEMENT -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <h3 style="font-size:14px; font-weight:700; margin-bottom:14px;">Règlement de la facture</h3>
      <p style="font-weight:700; margin-bottom:6px; font-size:12px;">Virement Interac :</p>
      <p style="font-size:11px; line-height:1.7;">Pour acquitter cette facture, vous pouvez effectuer un virement Interac à l'adresse courriel de l'entreprise : <strong>${ent.email || 'info@peintureresidentielleqc.com'}</strong></p>
      <p style="font-size:11px; margin-top:4px;">Aucun mot de passe ne sera requis.</p>
      <p style="font-weight:700; margin-top:14px; margin-bottom:6px; font-size:12px;">Chèque :</p>
      <p style="font-size:11px; line-height:1.7;">Vous pouvez acquitter cette facture par chèque en l'adressant à :</p>
      <p style="font-size:11px; margin-top:4px; line-height:1.8;">
        ${ent.nom_entreprise || 'Peinture Résidentielle Qc Inc.'}<br>
        ${ent.adresse || ''}<br>
        ${ent.ville || ''} ${ent.code_postal || ''}
      </p>
    </div>

    <!-- TERMES ET CONDITIONS -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:20px; margin-bottom:16px;">
      <h3 style="font-size:14px; font-weight:700; margin-bottom:12px;">Termes et conditions</h3>
      <ul style="list-style:none; padding:0; margin:0; font-size:11px; line-height:2;">
        <li style="padding:3px 0; border-bottom:1px dotted #e5e7eb;">• Tous les prix ci-haut mentionnés sont en dollars Canadiens.</li>
        <li style="padding:3px 0; border-bottom:1px dotted #e5e7eb;">• Paiement net 3 jours.</li>
        <li style="padding:3px 0;">• Frais d'administration de 2% par mois sur tout compte passé dû.</li>
      </ul>
    </div>

    <!-- REMERCIEMENTS -->
    <div style="border:1px solid #e5e7eb; border-radius:8px; padding:16px; margin-bottom:16px; background:#f0fdf4; text-align:center;">
      <p style="font-style:italic; color:#374151; font-weight:600; font-size:12px;">
        Nous vous remercions de votre confiance !
      </p>
    </div>
  </div>
</body>
</html>`;
}

// ─── Client Config Modal ────────────────────────────────────────────────
function ClientConfigModal({ open, onClose }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/lmds/client-config`, { headers: authHeaders() })
        .then(({ data }) => setConfig(data))
        .catch(() => toast.error("Erreur chargement"));
    }
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/lmds/client-config`, config, { headers: authHeaders() });
      toast.success("Client LMDS sauvegardé");
      onClose();
    } catch (e) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (!config) return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent><div className="py-8 text-center text-slate-400">Chargement...</div></DialogContent>
    </Dialog>
  );
  const upd = (k, v) => setConfig(prev => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="client-config-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" /> Client LMDS</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div><Label className="text-xs">Nom de l'entreprise</Label><Input value={config.nom_entreprise || ""} onChange={e => upd("nom_entreprise", e.target.value)} data-testid="client-nom" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Contact</Label><Input value={config.contact_nom || ""} onChange={e => upd("contact_nom", e.target.value)} /></div>
            <div><Label className="text-xs">Poste</Label><Input value={config.contact_poste || ""} onChange={e => upd("contact_poste", e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">Adresse</Label><Input value={config.adresse || ""} onChange={e => upd("adresse", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Ville</Label><Input value={config.ville || ""} onChange={e => upd("ville", e.target.value)} /></div>
            <div><Label className="text-xs">Province</Label><Input value={config.province || ""} onChange={e => upd("province", e.target.value)} /></div>
            <div><Label className="text-xs">Code postal</Label><Input value={config.code_postal || ""} onChange={e => upd("code_postal", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Téléphone</Label><Input value={config.telephone || ""} onChange={e => upd("telephone", e.target.value)} /></div>
            <div><Label className="text-xs">Courriel</Label><Input value={config.email || ""} onChange={e => upd("email", e.target.value)} data-testid="client-email" /></div>
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1" data-testid="save-client-config">{saving ? "..." : "Sauvegarder"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Invoice Preview Modal ──────────────────────────────────────────────
function InvoicePreviewModal({ open, onClose, installerId, annee, mois, onSent }) {
  // Permissions granulaires — déclarées ici car InvoicePreviewModal est un composant indépendant
  const { canDo: _canDo } = usePermissions() || {};
  const canSauvegarderBrouillon = _canDo?.('lmds_fact_sauvegarder_brouillon') ?? true;
  const canSupprimer            = _canDo?.('lmds_fact_supprimer')             ?? true;
  const canModifierNotes        = _canDo?.('lmds_fact_modifier_notes')        ?? true;
  const canGenerer              = _canDo?.('lmds_fact_generer')               ?? true;
  const canAjuster              = _canDo?.('lmds_fact_ajuster')               ?? true;
  const canImprimerPDF          = _canDo?.('lmds_fact_imprimer_pdf')          ?? true;
  const canAjouterFichier       = _canDo?.('lmds_fact_ajouter_fichier')       ?? true;
  const canEnvoyer              = _canDo?.('lmds_fact_envoyer')               ?? true;
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [editedLines, setEditedLines] = useState([]);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [adjustMode, setAdjustMode] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false); // 🆕 État pour l'aperçu email
  const [emailDestinataire, setEmailDestinataire] = useState("");  // 🆕 État pour l'email éditable

  const loadPreview = useCallback(async () => {
    if (!installerId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/lmds/facture/${installerId}/${annee}/${mois}/preview`, { headers: authHeaders() });
      setPreview(data);
      setNotes(data.facture?.notes || "");
      setEditedLines((data.facture?.lignes || []).map(l => ({ ...l })));
      if (data.facture?.ajuste) setAdjustAmount(String(data.facture.sous_total));
    } catch (e) { 
      console.error("Erreur chargement facture:", e);
      toast.error(e.response?.data?.detail || "Impossible de charger la facture"); 
      setPreview(null);
    }
    finally { setLoading(false); }
  }, [installerId, annee, mois]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

  // 🆕 Supprimer une ligne de service call
  const supprimerLigne = (index) => {
    const nouvelle = editedLines.filter((_, i) => i !== index);
    setEditedLines(nouvelle);
    toast.success("Ligne supprimée (pensez à sauvegarder le brouillon)");
  };

  const generer = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/lmds/facture/${installerId}/${annee}/${mois}/generer`, { notes, lignes: editedLines }, { headers: authHeaders() });
      toast.success(`Facture ${data.facture.numero_facture} générée`);
      loadPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setGenerating(false); }
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      await axios.post(`${API}/api/lmds/facture/${installerId}/${annee}/${mois}/save-draft`, { notes, lignes: editedLines }, { headers: authHeaders() });
      toast.success("Brouillon sauvegardé");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSavingDraft(false); }
  };

  const deleteFact = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/lmds/facture/${installerId}/${annee}/${mois}`, { headers: authHeaders() });
      toast.success("Facture supprimée — vous pouvez la regénérer");
      setConfirmDelete(false);
      loadPreview();
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setDeleting(false); }
  };

  const envoyer = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/lmds/facture/${installerId}/${annee}/${mois}/envoyer-facture`,
        { notes, to_email: emailDestinataire || preview?.client?.email || "" },  // ✅ FIX: Utiliser l'email éditable
        { headers: authHeaders() }
      );
      toast.success(`Facture ${data.numero_facture} envoyée !`);
      setShowEmailPreview(false); // Fermer l'aperçu
      onSent?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSending(false); }
  };

  // 🆕 Ouvrir l'aperçu d'envoi
  const ouvrirApercuEnvoi = () => {
    // ✅ Initialiser l'email avec celui du client LMDS
    setEmailDestinataire(preview?.client?.email || "");
    setShowEmailPreview(true);
  };

  const ajuster = async () => {
    if (!adjustAmount) return;
    setAdjusting(true);
    try {
      const { data } = await axios.put(`${API}/api/lmds/facture/${installerId}/${annee}/${mois}/ajuster`,
        { montant_ajuste: parseFloat(adjustAmount), raison: adjustReason, notes },
        { headers: authHeaders() }
      );
      toast.success(`Facture ajustée — Part installateur: ${fmt(data.part_installateur)}`);
      setAdjustMode(false);
      loadPreview();
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setAdjusting(false); }
  };

  const openPrint = () => {
    if (!preview) return;
    const html = generateLMDSInvoiceHTML(preview);
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const uploadConfirmation = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/api/lmds/facture/${installerId}/${annee}/${mois}/upload-confirmation`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`"${file.name}" téléversé`);
      loadPreview();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  if (!open) return null;
  const f = preview?.facture;
  const inst = preview?.installer || {};
  const statut = f?.statut || "brouillon";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <FileText className="w-5 h-5" />
            <span>Facture — {inst.prenom} {inst.nom} — {MOIS_FR[mois]} {annee}</span>
            {f?.numero && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUT_COLORS[statut] || STATUT_COLORS.brouillon}`}>{STATUT_LABELS[statut] || statut}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#1a237e]" /></div>
        ) : !preview || !f ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune donnée de facture disponible.</p>
            <p className="text-xs text-slate-400 mt-1">Vérifiez que l'installateur a des service calls pour cette période.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Quick info bar */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] text-slate-400 uppercase">Sous-total (HT)</p>
                <p className={`text-lg font-bold ${f.ajuste ? "text-amber-600" : "text-slate-800"}`}>{fmt(f.sous_total)}</p>
                {f.ajuste && <p className="text-[10px] text-slate-400 line-through">{fmt(f.sous_total_original)}</p>}
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] text-slate-400 uppercase">Total TTC</p>
                {/* total_avec_taxes est déjà le montant ajusté quand ajuste=True (calculé par le backend depuis montant_ajuste) */}
                <p className="text-lg font-bold text-[#1a237e]">{fmt(f.total_avec_taxes)}</p>
                {f.ajuste && f.sous_total_original && (
                  <p className="text-[10px] text-slate-400 line-through">{fmt(f.sous_total_original * 1.14975)}</p>
                )}
              </div>
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-[10px] text-emerald-600 uppercase">Part installateur ({Math.round((inst.taux || 0.65) * 100)}%) HT</p>
                <p className="text-lg font-bold text-emerald-700">{fmt(f.part_installateur)}</p>
              </div>
            </div>

            {/* Lignes de la facture — descriptions editables */}
            {editedLines.length > 0 && !f.numero && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs text-slate-500">Lignes de la facture (descriptions modifiables)</Label>
                  {canSauvegarderBrouillon && (
                    <Button variant="outline" size="sm" onClick={saveDraft} disabled={savingDraft} data-testid="save-draft-btn" className="h-7 text-xs">
                      {savingDraft ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
                      Sauvegarder brouillon
                    </Button>
                  )}
                </div>
                <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-slate-50 border-b text-slate-500">
                      <th className="px-2 py-1.5 text-left">Sem.</th>
                      <th className="px-2 py-1.5 text-left">Client</th>
                      <th className="px-2 py-1.5 text-left">Description</th>
                      <th className="px-2 py-1.5 text-right">SC</th>
                      <th className="px-2 py-1.5 text-right">Km</th>
                      <th className="px-2 py-1.5 text-center w-12"></th>
                    </tr></thead>
                    <tbody>
                      {editedLines.map((l, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50/50">
                          <td className="px-2 py-1 text-slate-400">S.{l.semaine}</td>
                          <td className="px-2 py-1 text-sm">{l.nom_client || "—"}</td>
                          <td className="px-2 py-1">
                            <input
                              value={l.description || ""}
                              onChange={e => {
                                const next = [...editedLines];
                                next[i] = { ...next[i], description: e.target.value };
                                setEditedLines(next);
                              }}
                              className="w-full text-xs border rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-300"
                            />
                          </td>
                          <td className="px-2 py-1 text-right">{l.service_call > 0 ? fmt(l.service_call) : "0,00 $"}</td>
                          <td className="px-2 py-1 text-right">{l.km_montant > 0 ? fmt(l.km_montant) : "—"}</td>
                          <td className="px-2 py-1 text-center">
                            <button
                              onClick={() => supprimerLigne(i)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                              title="Supprimer cette ligne"
                              data-testid={`delete-line-${i}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-xs text-slate-500">Notes sur la facture</Label>
              <Textarea 
                value={notes} 
                onChange={e => setNotes(e.target.value)} 
                placeholder="Notes visibles sur la facture (texte libre)" 
                rows={3} 
                className="mt-1" 
                data-testid="invoice-notes"
                disabled={!canModifierNotes}
              />
            </div>

            {/* Adjustment panel */}
            {canAjuster && f.numero && !adjustMode && (
              <button onClick={() => { setAdjustMode(true); setAdjustAmount(String(f.sous_total)); }}
                className="text-sm text-amber-600 hover:text-amber-700 underline flex items-center gap-1" data-testid="adjust-btn">
                <DollarSign className="w-3.5 h-3.5" /> Ajuster la facture (paiement partiel)
              </button>
            )}
            {adjustMode && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-amber-800">Ajustement de facture</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Montant ajusté (avant taxes)</Label>
                    <Input type="number" step="0.01" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} data-testid="adjust-amount" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Part installateur ({Math.round((inst.taux || 0.65) * 100)}%)</Label>
                    <p className="mt-2 text-lg font-bold text-emerald-700">{fmt((parseFloat(adjustAmount) || 0) * (inst.taux || 0.65))}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Raison</Label>
                  <Input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Ex: Client a contesté 1 ligne" className="mt-1" data-testid="adjust-reason" />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAdjustMode(false)}>Annuler</Button>
                  <Button size="sm" onClick={ajuster} disabled={adjusting} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-adjust-btn">
                    {adjusting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />} Confirmer
                  </Button>
                </div>
              </div>
            )}

            {/* Confirmations */}
            {f.numero && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Confirmations de paiement</p>
                  {canAjouterFichier && (
                    <label className="flex items-center gap-1.5 text-xs text-[#1a237e] hover:underline cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> {uploading ? "..." : "Ajouter"}
                      <input type="file" className="hidden" onChange={uploadConfirmation} disabled={uploading} data-testid="upload-confirmation" />
                    </label>
                  )}
                </div>
                {f.confirmations?.length > 0 ? f.confirmations.map((c, i) => (
                  <a key={c.id || c._id || i} href={urlFichier(`/api/lmds/confirmation-file/${c.path}`)} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 text-xs" data-testid={`confirmation-file-${i}`}>
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{c.filename}</span>
                    <span className="text-slate-400 ml-auto">{new Date(c.uploaded_at).toLocaleDateString("fr-CA")}</span>
                  </a>
                )) : <p className="text-xs text-slate-400">Aucun fichier</p>}
              </div>
            )}

            {/* Statut de la facture — indicateur clair */}
            {f.numero && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between" data-testid="facture-generee-indicator">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">Facture {f.numero} générée</p>
                    <p className="text-xs text-blue-600">{f.date_facture ? `Le ${new Date(f.date_facture + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}</p>
                  </div>
                </div>
                {canSupprimer && !f.deja_envoye && !confirmDelete && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs" data-testid="delete-facture-btn">
                    <X className="w-3.5 h-3.5 mr-1" /> Supprimer
                  </Button>
                )}
                {canSupprimer && confirmDelete && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 font-medium">Confirmer ?</span>
                    <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)} className="h-7 text-xs">Non</Button>
                    <Button size="sm" onClick={deleteFact} disabled={deleting} className="bg-red-600 hover:bg-red-700 h-7 text-xs" data-testid="confirm-delete-facture-btn">
                      {deleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Oui, supprimer
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={onClose}>Fermer</Button>
              {canImprimerPDF && f.numero && (
                <Button variant="outline" onClick={openPrint} data-testid="print-facture-btn" className="flex items-center gap-1.5">
                  <Printer className="w-4 h-4" /> Imprimer / PDF
                </Button>
              )}
              {canGenerer && !f.numero && (
                <Button onClick={generer} disabled={generating} className="bg-[#1a237e] hover:bg-[#283593] flex-1" data-testid="generer-facture-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />} Générer
                </Button>
              )}
              {canEnvoyer && f.numero && (
                <Button onClick={ouvrirApercuEnvoi} disabled={!preview?.client?.email} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="envoyer-facture-lmds-btn">
                  <Send className="w-4 h-4 mr-2" />
                  {f.deja_envoye ? "Renvoyer" : "Envoyer"}
                </Button>
              )}
            </div>

            {f.deja_envoye && <p className="text-xs text-emerald-600 text-center flex items-center justify-center gap-1"><CheckCircle2 className="w-3 h-3" /> Envoyée le {new Date(f.date_envoi).toLocaleDateString("fr-CA")}</p>}
            {f.numero && !preview?.client?.email && <p className="text-xs text-amber-600 text-center">Configurez le courriel du client LMDS pour envoyer</p>}
          </div>
        )}
      </DialogContent>

      {/* 🆕 Dialog d'aperçu d'envoi */}
      {showEmailPreview && preview && (
        <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Aperçu de l'envoi — Facture Service Call
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Informations d'envoi */}
              <div className="bg-slate-50 border rounded-lg p-4 space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm text-slate-600">Destinataire (email)</Label>
                  <Input 
                    type="email" 
                    value={emailDestinataire} 
                    onChange={e => setEmailDestinataire(e.target.value)}
                    placeholder="ex: tporcu@blindstogo.com"
                    className="h-9"
                  />
                  <p className="text-xs text-slate-500">💡 Vous pouvez modifier l'adresse email pour cet envoi</p>
                </div>
                <div className="flex items-center justify-between text-sm pt-2 border-t">
                  <span className="text-slate-600">Sujet :</span>
                  <span className="font-medium text-xs">Facture Service Call {preview?.facture?.numero} — {MOIS_FR[mois]} {annee}</span>
                </div>
              </div>

              {/* Notes optionnelles */}
              <div>
                <Label className="text-sm">Message optionnel (sera ajouté dans l'email)</Label>
                <Textarea 
                  value={notes} 
                  onChange={e => setNotes(e.target.value)} 
                  placeholder="Ajouter un message personnalisé..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              {/* Aperçu de la facture (HTML) */}
              <div>
                <Label className="text-sm mb-2 block">Aperçu de la facture jointe (PDF)</Label>
                <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto bg-white">
                  <div 
                    dangerouslySetInnerHTML={{ __html: generateLMDSInvoiceHTML(preview) }}
                    className="p-4"
                    style={{ transform: 'scale(0.8)', transformOrigin: 'top left', width: '125%' }}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEmailPreview(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={envoyer} 
                  disabled={sending || !emailDestinataire || !emailDestinataire.includes('@')} 
                  className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Confirmer l'envoi
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

// ─── Invoice History Panel ──────────────────────────────────────────────
function InvoiceHistory() {
  const [envois, setEnvois] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/lmds/factures-envois`, { headers: authHeaders() })
      .then(({ data }) => setEnvois(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!envois.length) return <div className="text-center py-8 text-slate-400"><Mail className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">Aucune facture envoyée</p></div>;

  return (
    <div className="space-y-2" data-testid="invoice-history">
      {envois.map((e, i) => (
        <div key={e.id || e._id || i} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${e.statut === "envoye" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
              {e.statut === "envoye" ? <CheckCircle2 className="w-4 h-4" /> : <X className="w-4 h-4" />}
            </div>
            <div>
              <p className="text-sm font-medium">{e.numero_facture} — {e.installer_nom}</p>
              <p className="text-xs text-slate-400">{MOIS_FR[e.mois]} {e.annee} · {fmt(e.montant_total)} · {e.to_email}</p>
            </div>
          </div>
          <p className="text-xs text-slate-500">{new Date(e.date_envoi).toLocaleDateString("fr-CA")}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────
export function LMDSFacturationGlobale() {
  const navigate = useNavigate();
  const { permissions, user, isProprietaire } = useAuth();
  const { canDo } = usePermissions();
  const now = new Date();
  const [annee, setAnnee] = useState(now.getFullYear());
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showClientConfig, setShowClientConfig] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [activeTab, setActiveTab] = useState("facturation");

  const canSeeHistory = permissions?.actions?.lmds_voir_historique_factures !== false;
  const peutPayer = canDo('lmds_facturation_payer');
  const voirPropresSeulement = !isProprietaire && canDo('lmds_facturation_voir_propres_seulement');
  
  // 🆕 Permissions granulaires pour Facturation Service Call (avec fallback sûr)
  const canVoirClientLMDS = canDo('lmds_fact_voir_client_lmds') ?? true;
  const canSauvegarderBrouillon = canDo('lmds_fact_sauvegarder_brouillon') ?? true;
  const canSupprimer = canDo('lmds_fact_supprimer') ?? true;
  const canModifierNotes = canDo('lmds_fact_modifier_notes') ?? true;
  const canGenerer = canDo('lmds_fact_generer') ?? true;
  const canAjuster = canDo('lmds_fact_ajuster') ?? true;
  const canImprimerPDF = canDo('lmds_fact_imprimer_pdf') ?? true;
  const canAjouterFichier = canDo('lmds_fact_ajouter_fichier') ?? true;
  const canEnvoyer = canDo('lmds_fact_envoyer') ?? true;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/api/lmds/facturation-globale/${annee}/${mois}`, { headers: authHeaders() });
      
      // 🆕 Filtrer les factures si l'utilisateur ne peut voir que les siennes
      if (voirPropresSeulement && user?.employee_id && d?.installers) {
        d.installers = d.installers.filter(inst => inst.installer_id === user.employee_id);
        // Recalculer tous les totaux après filtrage
        d.total_global_sc = d.installers.reduce((sum, i) => sum + (i.total_service_calls || 0), 0);
        d.total_global_km = d.installers.reduce((sum, i) => sum + (i.total_km || 0), 0);
        d.total_global = d.total_global_sc + d.total_global_km;
        d.total_paye = d.installers.reduce((sum, i) => sum + (i.payee ? (i.total_ajuste ?? i.total ?? 0) : 0), 0);
        d.total_impaye = d.total_global - d.total_paye;
      }
      
      setData(d);
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Erreur chargement"); }
    finally { setLoading(false); }
  }, [annee, mois, voirPropresSeulement, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function togglePaiement(inst) {
    // Demander confirmation avant de RETIRER le statut payé (mais pas pour payer)
    if (inst.payee) {
      const ok = window.confirm(`Êtes-vous sûr de vouloir retirer le statut "Payé" pour ${inst.prenom} ${inst.nom} ?`);
      if (!ok) return;
    }
    setPayingId(inst.installer_id);
    try {
      // ✅ Utiliser la PART INSTALLATEUR (65%) du montant ajusté AVEC TAXES
      const totalBase = inst.ajuste ? inst.total_ajuste : inst.total;
      const taux = inst.taux || 0.65; // Taux de commission (65% par défaut)
      const partHT = totalBase * taux; // Part installateur HT
      const partTTC = partHT * 1.14975; // Part installateur avec taxes (TPS + TVQ)
      
      await axios.post(`${API}/api/lmds/facture/${inst.installer_id}/${annee}/${mois}/payer`,
        { payee: !inst.payee, montant: partHT }, // On envoie HT, le backend calcule TTC pour le SMS
        { headers: authHeaders() }
      );
      toast.success(inst.payee ? `${inst.prenom} marqué impayé` : `${inst.prenom} marqué payé`);
      fetchData();
    } catch (e) { toast.error("Erreur"); }
    finally { setPayingId(null); }
  }

  const nbPaye = data?.installers?.filter(i => i.payee).length || 0;
  const nbTotal = data?.installers?.length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <Store className="w-4 h-4" />
            <button onClick={() => navigate("/sous-traitants/lmds")} className="hover:text-slate-700">Sous-traitants</button>
            <ChevronRight className="w-3 h-3" /><span>Facturation SC</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Facturation Service Call — Vue globale</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={mois} onChange={e => setMois(Number(e.target.value))} data-testid="mois-select"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30">
            {MOIS_FR.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <select value={annee} onChange={e => setAnnee(Number(e.target.value))} data-testid="annee-select"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1a237e]/30">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {canVoirClientLMDS && (
            <button onClick={() => setShowClientConfig(true)} data-testid="config-client-btn"
              className="flex items-center gap-1.5 border border-slate-200 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
              <Settings className="w-4 h-4" /> Client LMDS
            </button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="facturation" data-testid="tab-facturation">Facturation</TabsTrigger>
          {canSeeHistory && <TabsTrigger value="historique" data-testid="tab-historique">Historique envois</TabsTrigger>}
        </TabsList>

        <TabsContent value="facturation" className="space-y-6 mt-4">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]" /></div>
          ) : (
            <>
              {data && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Service Calls</p>
                    <p className="text-2xl font-bold text-red-600 mt-1" data-testid="total-sc">{fmt(data.total_global_sc)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Kilométrage</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="total-km">{fmt(data.total_global_km)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Total</p>
                    <p className="text-2xl font-bold text-[#1a237e] mt-1" data-testid="total-global">{fmt(data.total_global)}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Paiements</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-2xl font-bold text-emerald-600" data-testid="total-paye">{fmt(data.total_paye)}</p>
                      <span className="text-xs text-slate-400">/ {fmt(data.total_global)}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${data.total_global > 0 ? (data.total_paye / data.total_global * 100) : 0}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 font-medium">{nbPaye}/{nbTotal}</span>
                    </div>
                  </div>
                </div>
              )}

              {!data?.installers?.length ? (
                <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">Aucun service call pour {MOIS_FR[mois]} {annee}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.installers.map(inst => {
                    const isExpanded = expandedId === inst.installer_id;
                    return (
                      <div key={inst.installer_id} className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors ${inst.payee ? "border-emerald-200" : "border-slate-200"}`}
                        data-testid={`facture-installer-${inst.installer_id}`}>
                        <div className={`flex items-center justify-between px-5 py-3.5 ${inst.payee ? "bg-emerald-700" : "bg-[#1a237e]"}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${inst.payee ? "bg-emerald-500" : "bg-white/20"}`}>
                              {inst.payee ? <CheckCircle2 className="w-4 h-4" /> : <>{inst.prenom[0]}{inst.nom[0]}</>}
                            </div>
                            <div>
                              <span className="font-semibold text-white">{inst.prenom} {inst.nom}</span>
                              <span className="text-blue-200 text-xs ml-2">{Math.round((inst.taux || 0.65) * 100)}%</span>
                              {inst.payee && <span className="ml-2 inline-flex items-center gap-1 text-xs bg-emerald-500/30 text-emerald-100 px-2 py-0.5 rounded-full">Payé</span>}
                              {inst.ajuste && (
                                <span
                                  className="ml-2 inline-flex items-center gap-1 text-xs bg-amber-500/30 text-amber-200 border border-amber-400/40 px-2 py-0.5 rounded-full"
                                  title={inst.raison_ajustement || "Montant ajusté"}
                                >
                                  Ajusté{inst.date_ajustement ? ` · ${inst.date_ajustement}` : ""}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* SC et KM */}
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-blue-200">SC</p>
                              <p className="text-sm font-semibold text-white">{fmt(inst.total_service_calls)}</p>
                            </div>
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-blue-200">KM</p>
                              <p className="text-sm font-semibold text-white">{fmt(inst.total_km)}</p>
                            </div>
                            
                            {/* ═══ Si AJUSTÉ : montants originaux en ROUGE + montants ajustés en VERT ═══ */}
                            {inst.ajuste && inst.total_ajuste !== inst.total ? (
                              <>
                                {/* Montants ORIGINAUX en ROUGE (barré) */}
                                <div className="text-right">
                                  <p className="text-xs text-red-300">Total HT</p>
                                  <p className="text-sm font-semibold text-red-400 line-through">{fmt(inst.total)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-red-300">Part ({Math.round((inst.taux || 0.65) * 100)}%) HT</p>
                                  <p className="text-sm font-semibold text-red-400 line-through">{fmt(inst.total * (inst.taux || 0.65))}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-red-300">Part ({Math.round((inst.taux || 0.65) * 100)}%) TTC</p>
                                  <p className="text-sm font-semibold text-red-400 line-through">{fmt(inst.total * (inst.taux || 0.65) * 1.14975)}</p>
                                </div>
                                
                                {/* Séparateur visuel */}
                                <div className="w-px h-12 bg-blue-400/30 mx-1"></div>
                                
                                {/* Montants AJUSTÉS en VERT */}
                                <div className="text-right">
                                  <p className="text-xs text-emerald-200">Ajusté HT</p>
                                  <p className="text-sm font-bold text-emerald-300">{fmt(inst.total_ajuste)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-emerald-200">Part ({Math.round((inst.taux || 0.65) * 100)}%) HT</p>
                                  <p className="text-sm font-bold text-emerald-300">{fmt(inst.total_ajuste * (inst.taux || 0.65))}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-emerald-200">Part ({Math.round((inst.taux || 0.65) * 100)}%) TTC</p>
                                  <p className="text-lg font-bold text-emerald-300">{fmt((inst.total_ajuste_ttc ?? inst.total_ajuste * 1.14975) * (inst.taux || 0.65))}</p>
                                </div>
                              </>
                            ) : (
                              /* ═══ Si PAS AJUSTÉ : affichage normal ═══ */
                              <>
                                <div className="text-right">
                                  <p className="text-xs text-blue-200">Total HT</p>
                                  <p className="text-sm font-semibold text-white">{fmt(inst.total)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-emerald-200">Part ({Math.round((inst.taux || 0.65) * 100)}%) HT</p>
                                  <p className="text-sm font-bold text-emerald-300">{fmt(inst.total * (inst.taux || 0.65))}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-xs text-emerald-200">Part ({Math.round((inst.taux || 0.65) * 100)}%) TTC</p>
                                  <p className="text-lg font-bold text-emerald-300">{fmt(inst.total * (inst.taux || 0.65) * 1.14975)}</p>
                                </div>
                              </>
                            )}
                            {peutPayer && (
                            <button onClick={() => togglePaiement(inst)} disabled={payingId === inst.installer_id}
                              data-testid={`toggle-paiement-${inst.installer_id}`}
                              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ${inst.payee ? "bg-emerald-500/30 hover:bg-red-500/30 text-emerald-100" : "bg-white/20 hover:bg-emerald-500/30 text-white"}`}>
                              {payingId === inst.installer_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : inst.payee ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                              {inst.payee ? "Payé" : "Payer"}
                            </button>
                            )}
                            <button onClick={() => setInvoicePreview(inst.installer_id)} data-testid={`voir-facture-${inst.installer_id}`}
                              className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors">
                              <Eye className="w-3.5 h-3.5" /> Facture
                            </button>
                            <button onClick={() => setExpandedId(isExpanded ? null : inst.installer_id)}
                              className="text-white/70 hover:text-white transition-colors p-1" data-testid={`toggle-detail-${inst.installer_id}`}>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        {isExpanded && inst.lignes.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead><tr className="bg-slate-50 text-slate-500 text-left">
                                <th className="px-4 py-2">Sem.</th><th className="px-4 py-2">Client</th><th className="px-4 py-2">Date</th>
                                <th className="px-4 py-2">SI</th><th className="px-4 py-2">Description</th>
                                <th className="px-4 py-2 text-right">SC</th><th className="px-4 py-2 text-right">Km</th><th className="px-4 py-2 text-right">Sous-total</th>
                              </tr></thead>
                              <tbody>{inst.lignes.map((l, i) => (
                                <tr key={l.id || l._id || i} className="border-t border-slate-100 hover:bg-slate-50">
                                  <td className="px-4 py-2 text-slate-400">S.{l.semaine}</td>
                                  <td className="px-4 py-2 font-medium">{l.nom_client || "—"}</td>
                                  <td className="px-4 py-2 text-slate-500">{l.date_rdv || "—"}</td>
                                  <td className="px-4 py-2 text-slate-500">{l.si || "—"}</td>
                                  <td className="px-4 py-2 text-slate-400">{l.description || "—"}</td>
                                  <td className="px-4 py-2 text-right text-red-600 font-medium">{l.service_call > 0 ? fmt(l.service_call) : "—"}</td>
                                  <td className="px-4 py-2 text-right text-blue-600">{l.km_montant > 0 ? fmt(l.km_montant) : "—"}</td>
                                  <td className="px-4 py-2 text-right font-semibold">{fmt(l.service_call + l.km_montant)}</td>
                                </tr>
                              ))}</tbody>
                              <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50">
                                <td colSpan={5} className="px-4 py-2 font-semibold text-slate-700 text-right">Total {inst.prenom} {inst.nom}</td>
                                <td className="px-4 py-2 text-right text-red-600 font-bold">{fmt(inst.total_service_calls)}</td>
                                <td className="px-4 py-2 text-right text-blue-600 font-bold">{fmt(inst.total_km)}</td>
                                <td className="px-4 py-2 text-right text-[#1a237e] font-bold">{fmt(inst.total)}</td>
                              </tr></tfoot>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {canSeeHistory && <TabsContent value="historique" className="mt-4"><InvoiceHistory /></TabsContent>}
      </Tabs>

      <ClientConfigModal open={showClientConfig} onClose={() => setShowClientConfig(false)} />
      <InvoicePreviewModal open={!!invoicePreview} onClose={() => setInvoicePreview(null)} installerId={invoicePreview} annee={annee} mois={mois} onSent={fetchData} />
    </div>
  );
}

export default LMDSFacturationGlobale;
